import React, { useState } from "react";
import {
  Table, Button, Space, Tag, message, Popconfirm, Modal,
  Form, Input, InputNumber, Drawer, Upload, Radio
} from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { useApiSelector } from 'src/hooks'
import { useRequest, dispatchAsync } from 'friday-async'
import type { DictionaryItem } from './apis'

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
};

const Dictionary = () => {
  const apis = useApiSelector();
  const { dataArray, isValidating, revalidate } = useRequest(apis.dictionary.list(), {
    paginated: false,
  })

  // 字典 CRUD 抽屉
  const [dictDrawer, setDictDrawer] = useState<{ visible: boolean; info: any }>({
    visible: false, info: null
  })

  // 字典项管理抽屉
  const [itemDrawer, setItemDrawer] = useState<{ visible: boolean; dict: any }>({
    visible: false, dict: null
  })

  // 字典项编辑弹窗
  const [itemModal, setItemModal] = useState<{ visible: boolean; info: any }>({
    visible: false, info: null
  })

  // xlsx 导入弹窗
  const [importVisible, setImportVisible] = useState(false)
  const [importLoading, setImportLoading] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importMode, setImportMode] = useState('replace')
  const [importResult, setImportResult] = useState<any>(null)

  const [dictForm] = Form.useForm()
  const [itemForm] = Form.useForm()

  const isEditDict = !!dictDrawer.info

  // ====== 字典 CRUD ======
  const handleDictCreate = () => setDictDrawer({ visible: true, info: null })
  const handleDictEdit = (record: any) => setDictDrawer({ visible: true, info: record })

  const handleDictSubmit = async () => {
    try {
      const vals = await dictForm.validateFields()
      let error: any
      if (isEditDict) {
        const r = await dispatchAsync(apis.dictionary.update({ _id: dictDrawer.info._id, ...vals }))
        error = r.error
      } else {
        const r = await dispatchAsync(apis.dictionary.create(vals))
        error = r.error
      }
      if (error) return message.error(isEditDict ? '编辑失败' : '创建失败')
      message.success(isEditDict ? '编辑成功' : '创建成功')
      setDictDrawer({ visible: false, info: null })
      dictForm.resetFields()
      revalidate()
    } catch (err) { console.log(err) }
  }

  const handleDictRemove = async (_id: string) => {
    const { error } = await dispatchAsync(apis.dictionary.remove({ _id }))
    if (error) return message.error('删除失败')
    message.success('删除成功')
    revalidate()
  }

  // ====== 字典项管理 ======
  const openItemManage = (record: any) => {
    setItemDrawer({ visible: true, dict: record })
  }

  // 新建/编辑字典项
  const handleItemModalOpen = (item?: any) => {
    setItemModal({ visible: true, info: item || null })
    if (item) {
      itemForm.setFieldsValue(item)
    }
  }

  const handleItemSubmit = async () => {
    try {
      const vals = await itemForm.validateFields()
      const dict = itemDrawer.dict
      let error: any

      if (itemModal.info) {
        const r = await dispatchAsync(apis.dictionary.updateItem({
          _id: dict._id,
          itemValue: itemModal.info.value,
          item: vals,
        }))
        error = r.error
      } else {
        const r = await dispatchAsync(apis.dictionary.addItem({
          _id: dict._id,
          item: vals,
        }))
        error = r.error
      }

      if (error) return message.error(itemModal.info ? '更新失败' : '添加失败')
      message.success(itemModal.info ? '更新成功' : '添加成功')
      setItemModal({ visible: false, info: null })
      itemForm.resetFields()
      revalidate()
    } catch (err) { console.log(err) }
  }

  const handleItemRemove = async (itemValue: string) => {
    const { error } = await dispatchAsync(apis.dictionary.removeItem({
      _id: itemDrawer.dict._id,
      itemValue,
    }))
    if (error) return message.error('删除失败')
    message.success('删除成功')
    // Refresh dictionary data silently
    revalidate()
  }

  // ====== xlsx 导入（在字典项管理抽屉内） ======
  const handleImportOpen = () => {
    setImportVisible(true)
    setImportFile(null)
    setImportResult(null)
  }

  const handleImportSubmit = async () => {
    try {
      if (!importFile) return message.warning('请选择文件')

      const dict = itemDrawer.dict
      setImportLoading(true)
      setImportResult(null)

      const formData = new FormData()
      formData.append('file', importFile)
      formData.append('dictCode', dict.code)
      formData.append('dictName', dict.name)
      formData.append('mode', importMode)

      const res = await fetch('/api/platform/dictionary/importXlsx', {
        method: 'POST',
        body: formData,
      })
      const json = await res.json()

      if (json.code !== 200) {
        message.error(json.message || '导入失败')
        setImportLoading(false)
        return
      }

      setImportResult(json.data)
      message.success(`导入成功：共 ${json.data.total} 行，成功 ${json.data.success} 行${json.data.skipped > 0 ? `，跳过 ${json.data.skipped} 行` : ''}`)
      revalidate()
      setImportLoading(false)
    } catch (err) {
      setImportLoading(false)
      console.log(err)
    }
  }

  // ====== 获取当前字典的 items（带排序） ======
  const currentItems: DictionaryItem[] = (itemDrawer.dict?.items || []).sort(
    (a: DictionaryItem, b: DictionaryItem) => (a.sort || 999) - (b.sort || 999)
  )

  const itemColumns = [
    { title: '标签', dataIndex: 'label', key: 'label', width: 150 },
    { title: '值', dataIndex: 'value', key: 'value', width: 150 },
    {
      title: '父级',
      dataIndex: 'parentValue',
      key: 'parentValue',
      width: 120,
      render: (v: string) => v || '-',
    },
    { title: '排序', dataIndex: 'sort', key: 'sort', width: 80 },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_: any, record: DictionaryItem) => {
        return (
        <Space>
          <Button type="link" size="small" icon={React.createElement(EditOutlined)} onClick={() => handleItemModalOpen(record)} />
          <Button type="link" size="small" danger icon={React.createElement(DeleteOutlined)} />
        </Space>
        );
      },
    },
  ]

  // ====== 字典列表列定义 ======
  const dictColumns = [
    { title: '标识', dataIndex: 'code', key: 'code', width: 150 },
    { title: '名称', dataIndex: 'name', key: 'name', width: 200 },
    {
      title: '数据项',
      dataIndex: 'items',
      key: 'items',
      width: 120,
      render: (items: any[]) => <Tag>{items?.length || 0} 项</Tag>,
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      render: (_: any, record: any) => (
        <Space>
          <Button type="link" size="small" onClick={() => openItemManage(record)}>
            管理数据项
          </Button>
          <Button type="link" size="small" onClick={() => handleDictEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除该字典？" onConfirm={() => handleDictRemove(record._id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Button type="primary" ghost icon={React.createElement(PlusOutlined)} onClick={handleDictCreate}>
        新建字典
      </Button>
      <Table
        rowKey="_id"
        columns={dictColumns}
        dataSource={dataArray}
        loading={isValidating}
        pagination={false}
      />

      {/* 字典 CRUD 抽屉 */}
      <Drawer
        title={isEditDict ? '编辑字典' : '新建字典'}
        width={480}
        open={dictDrawer.visible}
        onClose={() => {
          setDictDrawer({ visible: false, info: null })
          dictForm.resetFields()
        }}
        destroyOnClose
        footer={
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button style={{ width: 120 }} onClick={() => {
              setDictDrawer({ visible: false, info: null })
              dictForm.resetFields()
            }}>取消</Button>
            <Button style={{ width: 120 }} type="primary" onClick={handleDictSubmit}>确认</Button>
          </Space>
        }
      >
        <Form {...layout} form={dictForm}
          initialValues={isEditDict ? dictDrawer.info : {}}
        >
          <Form.Item name="code" label="标识" rules={[{ required: true, message: '请输入标识' }]}>
            <Input placeholder="如 province, city" disabled={isEditDict} />
          </Form.Item>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="如 省份, 城市" />
          </Form.Item>
        </Form>
      </Drawer>

      {/* 字典项管理抽屉 */}
      <Drawer
        title={`管理「${itemDrawer.dict?.name || ''}」数据项`}
        width={800}
        open={itemDrawer.visible}
        onClose={() => {
          setItemDrawer({ visible: false, dict: null })
          setItemModal({ visible: false, info: null })
        }}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <Button type="primary" ghost icon={React.createElement(PlusOutlined)}
              onClick={() => handleItemModalOpen()}>
              添加数据项
            </Button>
            <Button icon={React.createElement(UploadOutlined)} onClick={handleImportOpen}>
              导入 xlsx
            </Button>
          </Space>
          <Table
            rowKey="value"
            columns={itemColumns}
            dataSource={currentItems}
            pagination={false}
            size="small"
          />

          {/* xlsx 导入弹窗 */}
          <Modal
            title={`从 xlsx 导入「${itemDrawer.dict?.name || ''}」数据项`}
            open={importVisible}
            onOk={handleImportSubmit}
            onCancel={() => {
              setImportVisible(false)
              setImportResult(null)
            }}
            confirmLoading={importLoading}
            okText="导入"
            cancelText="取消"
            destroyOnClose
            width={480}
          >
            <div style={{ marginBottom: 16, padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>
              当前字典：<Tag>{itemDrawer.dict?.code}</Tag>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>导入模式</div>
              <Radio.Group value={importMode} onChange={(e) => setImportMode(e.target.value)}>
                <Radio value="replace">替换全部</Radio>
                <Radio value="append">追加</Radio>
              </Radio.Group>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>选择文件</div>
              <Upload
                accept=".xlsx,.xls"
                beforeUpload={(file) => { setImportFile(file); return false }}
                onRemove={() => setImportFile(null)}
                maxCount={1}
              >
                <Button icon={React.createElement(UploadOutlined)}>
                  {importFile ? importFile.name : '选择 xlsx 文件'}
                </Button>
              </Upload>
              <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                支持列名：标签/label/名称/name、值/value/编码/code、父级/parentValue/父级值（可选）
              </div>
            </div>
            {importResult && (
              <div style={{ padding: '8px 12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4 }}>
                <div style={{ fontWeight: 500, marginBottom: 4, color: '#52c41a' }}>导入结果</div>
                <div>总行数：{importResult.total}</div>
                <div>成功：{importResult.success}</div>
                {importResult.skipped > 0 && (
                  <div>
                    <div style={{ color: '#faad14', marginTop: 4 }}>跳过：{importResult.skipped} 行</div>
                    {importResult.skippedDetails?.length > 0 && (
                      <ul style={{ fontSize: 12, color: '#666', paddingLeft: 16, margin: '4px 0 0' }}>
                        {importResult.skippedDetails.map((msg: string, i: number) => (
                          <li key={i}>{msg}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
          </Modal>
        </Space>
      </Drawer>

      {/* 数据项编辑弹窗 */}
      <Modal
        title={itemModal.info ? '编辑数据项' : '添加数据项'}
        open={itemModal.visible}
        onOk={handleItemSubmit}
        onCancel={() => {
          setItemModal({ visible: false, info: null })
          itemForm.resetFields()
        }}
        okText="确认"
        cancelText="取消"
        destroyOnClose
      >
        <Form {...layout} form={itemForm} style={{ marginTop: 20 }}>
          <Form.Item name="label" label="标签" rules={[{ required: true, message: '请输入标签' }]}>
            <Input placeholder="如 广东省" />
          </Form.Item>
          <Form.Item name="value" label="值" rules={[{ required: true, message: '请输入值' }]}>
            <Input placeholder="如 guangdong" disabled={!!itemModal.info} />
          </Form.Item>
          <Form.Item name="parentValue" label="父级值">
            <Input placeholder="关联的父级词典项 value（可选）" />
          </Form.Item>
          <Form.Item name="sort" label="排序">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="排序序号" />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  )
}

export default Dictionary
